import { DynamicFormRefValues } from "../../../../../../../components/dynamicFormRef/types";
import { EvidenceData } from "../../../../../../../hooks/evidences/typyng";
import { Item, ObjectGeneric } from "../../../../../../../utils/types/generals";
import { OptionActionSavePayload, ReportSignatureData } from "../../../../../components/optionActions/types";
import { Answer, Question } from "../../../fragments/section/contextProvider/provider/types";
import { ActionPlanData } from "../../../typing/types";
import { SectionDefault, SectionList } from "../types";

const compareSection = ({ copy, section, header, headerCopy, config, configCopy }: {
    section?: SectionList;
    copy?: SectionList;
    header?: DynamicFormRefValues;
    headerCopy?: DynamicFormRefValues
    config?: OptionActionSavePayload
    configCopy?: OptionActionSavePayload
}) => {
    const isSameLength = copy?.length == section?.length
    const isSameQuestName = (headerCopy?.quest_name ?? '') == (header?.quest_name ?? '')
    const isSameQuestDescription = (headerCopy?.description ?? '') == (header?.description ?? '')
    const isSameQuestInstruction = (headerCopy?.instruction ?? '') == (header?.instruction ?? '')
    const isSameQuestTotal = (headerCopy?.totalPoints?.toString() ?? '') == (header?.totalPoints?.toString() ?? '')
    const isSameQuestionArea = ((headerCopy?.areas as Item)?.id ?? '') == ((header?.areas as Item)?.id ?? '')
    const isSameQuestionDepartment = ((headerCopy?.departments as Item)?.id ?? '') == ((header?.departments as Item)?.id ?? '')
    const isSameQuestionnaireConfig = !isSameConfig({ config, copy: configCopy })
    if (
        !isSameQuestName ||
        !isSameQuestDescription ||
        !isSameQuestInstruction ||
        !isSameQuestTotal ||
        !isSameQuestionArea ||
        !isSameQuestionDepartment ||
        !isSameQuestionnaireConfig ||
        !isSameLength
    ) { return true }
    return copy?.some((copy, index) => isSameSection({ copy, section: section?.[index] }))
}

const isSameSection = ({ copy, section }: { copy: SectionDefault; section?: SectionDefault }): boolean => {
    const isSameSectionName = (copy?.sectionDefault?.sectionName ?? '') == (section?.sectionDefault?.sectionName ?? '')
    const isSameSectionDescription = (copy?.sectionDefault?.description ?? '') == (section?.sectionDefault?.description ?? '')
    const isSameSectionPoints = (copy?.sectionDefault?.sectionPoints?.toString() ?? '') == (section?.sectionDefault?.sectionPoints?.toString() ?? '')
    const questionCopy = copy.questionListDefault
    const question = section?.questionListDefault
    const sameLength = questionCopy?.length == question?.length

    if (!isSameSectionName || !isSameSectionDescription || !isSameSectionPoints || !sameLength) { return true }
    return !!questionCopy?.some((copy, index) => isSameQuestion({ copy, question: question?.[index] }))
}

const isSameQuestion = ({ copy, question }: { copy?: Question; question?: Question }): boolean => {
    const isSameTitle = (copy?.question?.questionText ?? '') == (question?.question?.questionText ?? '')
    const isSameHelpText = (copy?.question?.helpText ?? '') == (question?.question?.helpText ?? '')
    const isSameTemplate = (copy?.question?.questionTemplate?.id ?? '') == (question?.question?.questionTemplate?.id ?? '')
    const isSameType = (copy?.question?.questionOption?.id ?? '') == (question?.question?.questionOption?.id ?? '')
    const isSameLength = (copy?.answer?.length ?? 0) == (question?.answer?.length ?? 0)
    const answer = question?.answer
    const config = JSON.parse(question?.configGeneral ?? '{}') as OptionActionSavePayload
    const configCopy = JSON.parse(copy?.configGeneral ?? '{}') as OptionActionSavePayload
    const isSameAnswerConfig = isSameConfig({ config, copy: configCopy, name: 'answer' })
    const configQuestion = JSON.parse(question?.question?.config ?? '{}') as OptionActionSavePayload
    const configCopyQuestion = JSON.parse(copy?.question?.config ?? '{}') as OptionActionSavePayload
    const isSameAnswerConfigQuestion = isSameConfig({ config: configQuestion, copy: configCopyQuestion, name: 'config'})

    if (!isSameTitle || !isSameHelpText || !isSameTemplate || !isSameType || !isSameLength || isSameAnswerConfig || isSameAnswerConfigQuestion) { return true }
    return !!copy?.answer?.some((copy, index) => isSameAnswer({ copy, answer: answer?.[index] }))
}

const isSameAnswer = ({ answer, copy, }: { copy?: Answer; answer?: Answer }) => {
    const isSameAnswer = (copy?.answer ?? '') == (answer?.answer ?? '')
    const isSameValue = (copy?.value?.toString() ?? '') == (answer?.value?.toString() ?? '')
    const isSameNotApplicable = (copy?.notApplicable?.toString() ?? '') == (answer?.notApplicable?.toString() ?? '')
    const isSameactiveReeportFolio = (copy?.activeReeportFolio?.toString() ?? '') == (answer?.activeReeportFolio?.toString() ?? '')
    const isSameCorrectOption = !!copy?.switchOption == !!answer?.switchOption
    const isSameAction = (copy?.action?.id ?? '') == (answer?.action?.id ?? '')
    const isSameAutoActionPlan = !!copy?.autoActionPlan == !!answer?.autoActionPlan
    const config = JSON.parse(answer?.config ?? '{}') as OptionActionSavePayload
    const configCopy = JSON.parse(copy?.config ?? '{}') as OptionActionSavePayload
    const isSameAnswerConfig = isSameConfig({ config, copy: configCopy })
    return (!isSameAnswer || !isSameValue || !isSameactiveReeportFolio || !isSameCorrectOption || !isSameAction || isSameAnswerConfig || !isSameNotApplicable || !isSameAutoActionPlan)
}

const isSameConfig = ({ config, copy, name }: {
    config?: OptionActionSavePayload
    copy?: OptionActionSavePayload
    name?:string
}): boolean => {
    const isSameActionPlanConfig = isSameActionPlan({ actionPlan: config?.actionPlan, copy: copy?.actionPlan })
    const isSameCommentConfig = isSameComment({ comment: config?.comment, copy: copy?.comment })
    const isSameDataFolio = validateObject({ copy: copy?.reportFolio, data: config?.reportFolio })
    const isSameConfigEvidence = !isSameEvidence({ copy: copy?.evidenceData, evidence: config?.evidenceData, name })
    const isSameRecurrence = validateObject({ copy: copy?.reincidence, data: config?.reincidence })
    const isSameSignature = isSameSignatureConfig({copy:copy?.reportSignature, signature:config?.reportSignature})
    const isSameAutoActionPlan = !!config?.autoActionPlan == !!copy?.autoActionPlan
    return !isSameSignature || isSameActionPlanConfig || isSameCommentConfig || isSameRecurrence || !isSameConfigEvidence || isSameDataFolio || !isSameAutoActionPlan
}

const isSameActionPlan = ({ actionPlan, copy }: { actionPlan?: ActionPlanData; copy?: ActionPlanData }): boolean => {
    const isSameData = validateObject({ copy: copy?.data, data: actionPlan?.data })
    const isSameDataFolio = validateObject({ copy: copy?.dataFolios, data: actionPlan?.dataFolios })
    const isSameDescription = validateObject({ copy: copy?.description, data: actionPlan?.description })
    const isSameActionPlanEvidence = !isSameEvidence({ copy: copy?.evidence, evidence: actionPlan?.evidence })
    const isSameActionPlanDataFolioEvidence = !isSameEvidence({ copy: copy?.evidenceFolio, evidence: actionPlan?.evidenceFolio })
    const isEvidenceOptionConfig = actionPlan?.options?.evidences?.sameConfig == copy?.options?.evidences?.sameConfig

    return (isSameData || isSameDataFolio || isSameDescription || !isSameActionPlanEvidence || !isSameActionPlanDataFolioEvidence || !isEvidenceOptionConfig)
}

const validateObject = ({ copy, data }: { data?: ObjectGeneric; copy?: ObjectGeneric }): boolean => {
    if (!copy && !data) { return false }
    if ((!!copy && !data) || (!copy && !!data)) { return true }
    if ((Object.values(copy ?? {})?.length ?? 0) != (Object.values(data ?? {})?.length ?? 0)) { return true }
    return Object.entries(copy ?? {})?.some(([key, value]) => {
        if (typeof data?.[key] == 'object') { return validateObject({ copy: value, data: data?.[key] }) }
        return data?.[key] != value
    })
}
const isSameComment = ({ comment, copy, }: {
    comment?: {
        evidence: EvidenceData[];
        default?: DynamicFormRefValues;
    }
    copy?: {
        evidence: EvidenceData[];
        default?: DynamicFormRefValues;
    }
}): boolean => {
    const isSameCommentEvidence = !isSameEvidence({ copy: copy?.evidence, evidence: comment?.evidence })

    return (copy?.default?.comment != comment?.default?.comment) || !isSameCommentEvidence
}

const isSameSignatureConfig = ({ signature, copy, }: {
    signature?:  ReportSignatureData[]
    copy?:  ReportSignatureData[]
}): boolean => {
    return isSameSignatureArray(signature, copy);
}

const isSameSignatureArray = (
    a?: ReportSignatureData[],
    b?: ReportSignatureData[]
): boolean => {

    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;

    return a.every((itemA, index) => {
        const itemB = b[index];
        return (
            itemA.id === itemB.id &&
            itemA.requeired === itemB.requeired &&
            itemA.description === itemB.description &&
            itemA.addComment === itemB.addComment &&
            itemA.reqiredComment === itemB.reqiredComment
        );
    });
};

const isSameEvidence = ({ copy, evidence, name }: { evidence?: EvidenceData[]; copy?: EvidenceData[]; name?:string }): boolean => {
    const isSameLength = (copy?.length ?? 0) == (evidence?.length ?? 0)
    if (!isSameLength) { return true }
    const isSameEvidenceConfig = !evidence?.some((item, index) => isSameEvidenceItemConfig({ copy: copy?.[index], item }))
    return !isSameEvidenceConfig
}

const isSameEvidenceItemConfig = ({ copy, item }: { copy?: EvidenceData; item?: EvidenceData }): boolean => {
    const copyArray = Object.entries(copy ?? {})
    const isSameLength = (copyArray?.length ?? 0) == (Object.entries(item ?? {})?.length ?? 0)

    if (!isSameLength || !deepEqual(copy, item)) { return true }

    return copyArray?.some(([key, value]) => {
        if (typeof value == 'object') {
            return value?.value != item?.[key]?.value
        }
        return value != item?.[key]
    })
}

function deepEqual(obj1: any, obj2: any) {
    if (typeof obj1 === 'number' && typeof obj2 === 'number') {
        // Convertir ambos números a string antes de comparar
        return String(obj1) === String(obj2);
    }

    if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        // Verificar si tienen la misma cantidad de propiedades
        if (keys1.length !== keys2.length) {
            return false;
        }

        for (let key of keys1) {
            if (!keys2.includes(key)) {
                return false;
            }
            if (!deepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    }

    // Convertir ambos a string si uno de ellos es number
    if (typeof obj1 === 'number' || typeof obj2 === 'number') {
        return String(obj1) === String(obj2);
    }

    // Comparar otros tipos directamente
    return obj1 === obj2;
}
export { compareSection };
